import { ChevronDown, Eye, MousePointerClick, Sparkles, Ticket } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import type {
  DiscountFunnelCountsShape,
  FunnelConversionRatesShape,
} from "../../types/discount-analytics-types";

interface Props {
  funnel: DiscountFunnelCountsShape;
  conversionRates: FunnelConversionRatesShape;
}

interface FunnelStep {
  key: keyof DiscountFunnelCountsShape;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STEPS: FunnelStep[] = [
  { key: "bannerView", labelKey: "discounts.funnel.steps.bannerView", icon: Eye },
  { key: "codeApplied", labelKey: "discounts.funnel.steps.codeApplied", icon: Ticket },
  {
    key: "bookingStarted",
    labelKey: "discounts.funnel.steps.bookingStarted",
    icon: MousePointerClick,
  },
  { key: "bookingCompleted", labelKey: "discounts.funnel.steps.bookingCompleted", icon: Sparkles },
];

function formatNumber(n: number): string {
  return new Intl.NumberFormat().format(n);
}

function formatPercent(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

export function DiscountFunnel({ funnel, conversionRates }: Props) {
  const { t } = useTranslation("reports");

  const totalEvents = STEPS.reduce((sum, step) => sum + funnel[step.key], 0);

  if (totalEvents === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("discounts.funnel.title")}</CardTitle>
          <CardDescription>{t("discounts.funnel.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground italic">{t("discounts.funnel.emptyState")}</p>
        </CardContent>
      </Card>
    );
  }

  const stepConversions: (number | null)[] = [
    null,
    conversionRates.viewToApply,
    conversionRates.applyToBookingStarted,
    conversionRates.bookingStartedToCompleted,
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("discounts.funnel.title")}</CardTitle>
        <CardDescription>{t("discounts.funnel.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {STEPS.map((step, idx) => {
            const count = funnel[step.key];
            const Icon = step.icon;
            const conv = stepConversions[idx];
            return (
              <div key={step.key}>
                <div className="flex items-center justify-between gap-3 rounded-lg border bg-background p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t(step.labelKey)}</div>
                    </div>
                  </div>
                  <div className="text-lg font-semibold tabular-nums shrink-0">
                    {formatNumber(count)}
                  </div>
                </div>
                {idx < STEPS.length - 1 && conv !== null && (
                  <div className="flex items-center justify-center gap-1 py-1 text-[10px] text-muted-foreground">
                    <ChevronDown className="h-3 w-3" />
                    <span>{formatPercent(stepConversions[idx + 1] ?? 0)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-md border bg-muted/30 p-2">
            <div className="text-muted-foreground">{t("discounts.funnel.overallConversion")}</div>
            <div className="mt-1 font-semibold tabular-nums">
              {formatPercent(
                funnel.bannerView > 0 ? funnel.bookingCompleted / funnel.bannerView : 0
              )}
            </div>
          </div>
          <div className="rounded-md border bg-muted/30 p-2">
            <div className="text-muted-foreground">{t("discounts.funnel.codeRejected")}</div>
            <div className="mt-1 font-semibold tabular-nums">
              {formatNumber(funnel.codeRejected)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
